import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    service: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleServiceChange = (value) => {
    setFormData({
      ...formData,
      service: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || 'Thank you! We will contact you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          location: '',
          service: '',
          message: ''
        });
      } else {
        toast.error(data.error || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#0D47A1] to-[#1565C0] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-6">Let's Power Your Future Together</h1>
          <p className="text-xl text-gray-100 leading-relaxed">
            Get in touch with our solar experts for a free consultation
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="border-none shadow-2xl">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold text-[#0D47A1] mb-6">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-gray-700 font-medium mb-2 block">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your full name"
                      className="border-gray-300 focus:border-[#4CAF50] focus:ring-[#4CAF50]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-medium mb-2 block">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@example.com"
                      className="border-gray-300 focus:border-[#4CAF50] focus:ring-[#4CAF50]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-gray-700 font-medium mb-2 block">Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+91 XXXXX XXXXX"
                      className="border-gray-300 focus:border-[#4CAF50] focus:ring-[#4CAF50]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="location" className="text-gray-700 font-medium mb-2 block">Location *</Label>
                    <Input
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      required
                      placeholder="City, State"
                      className="border-gray-300 focus:border-[#4CAF50] focus:ring-[#4CAF50]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="service" className="text-gray-700 font-medium mb-2 block">Service Interest *</Label>
                    <Select onValueChange={handleServiceChange} value={formData.service}>
                      <SelectTrigger className="border-gray-300 focus:border-[#4CAF50] focus:ring-[#4CAF50]">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="residential">Residential Solar</SelectItem>
                        <SelectItem value="commercial">Commercial Solar</SelectItem>
                        <SelectItem value="industrial">Industrial Solar</SelectItem>
                        <SelectItem value="street-lights">Solar Street Lights</SelectItem>
                        <SelectItem value="water-heater">Solar Water Heater</SelectItem>
                        <SelectItem value="maintenance">Maintenance & AMC</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-gray-700 font-medium mb-2 block">Message / Requirements</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Tell us about your requirements..."
                      className="border-gray-300 focus:border-[#4CAF50] focus:ring-[#4CAF50]"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-[#FFC107] hover:bg-[#FFD54F] text-black font-semibold text-lg py-6 transition-all duration-300 hover:shadow-lg"
                  >
                    Submit Inquiry
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Information */}
             <div className="space-y-8">
  {/* Contact Card */}
  <Card className="border-none shadow-xl">
    <CardContent className="p-8">
      <h2 className="text-3xl font-bold text-[#0D47A1] mb-6">Contact Information</h2>
      <div className="space-y-6">
        {/* Phone / WhatsApp */}
        <div className="flex items-start gap-4">
          <Phone className="w-6 h-6 text-[#4CAF50] flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-bold text-gray-900 mb-1">Phone / WhatsApp</h3>
            <p className="text-gray-700">
              <a
                href="https://wa.me/91822367580"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#25D366]"
              >
                +91 8822367580
              </a>
              , +91 9940199674
            </p>
          </div>
        </div>

        {/* Email */}
        <div className="flex items-start gap-4">
          <Mail className="w-6 h-6 text-[#4CAF50] flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-bold text-gray-900 mb-1">Email</h3>
            <p className="text-gray-700">info@yoursenergies.com</p>
          </div>
        </div>

        {/* Address */}
        <div className="flex items-start gap-4">
          <MapPin className="w-6 h-6 text-[#4CAF50] flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-bold text-gray-900 mb-1">Address</h3>
            <p className="text-gray-700">
              <strong>Head Office:</strong> Beltola, Guwahati, Assam<br />
              <strong>Branch Office:</strong> Old Amulapatty, Sivasagar<br />
              India
            </p>
          </div>
        </div>

        {/* Business Hours */}
        <div className="flex items-start gap-4">
          <Clock className="w-6 h-6 text-[#4CAF50] flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-bold text-gray-900 mb-1">Business Hours</h3>
            <p className="text-gray-700">
              Monday - Saturday: 9:00 AM - 6:00 PM<br />
              Sunday: Closed
            </p>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>

  {/* Map Card */}
  <Card className="border-none shadow-xl overflow-hidden">
    <CardContent className="p-0">
      <div className="h-80 relative">
        {/* Embedded Google Map for Beltola, Guwahati */}
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3578.051054931329!2d91.78547617461609!3d26.11728269376456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x375a59f8d3c9b4ef%3A0x1a8e827ab6cb26e4!2sBeltola%2C%20Guwahati%2C%20Assam%2C%20India!5e0!3m2!1sen!2sin!4v1729151212345!5m2!1sen!2sin"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Head Office - Beltola, Guwahati"
        ></iframe>

        {/* Get Directions Button */}
        <div className="absolute bottom-4 right-4">
          <a
            href="https://www.google.com/maps/dir/?api=1&destination=26.1172827,91.7854761"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#0D47A1] text-white px-5 py-2 rounded-full shadow-lg hover:bg-[#1565C0] transition-all duration-300 text-sm font-semibold"
          >
            Get Directions
          </a>
        </div>
      </div>
    </CardContent>
  </Card>
</div>

          </div>
        </div>
      </section>

      {/* Additional CTA */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-[#0D47A1] mb-4">Have Questions?</h2>
          <p className="text-lg text-gray-700 mb-6">
            Our team is ready to help you make the switch to solar energy. Reach out today for a personalized consultation.
          </p>
          <p className="text-gray-600">
            Response time: Within 24 hours
          </p>
        </div>
      </section>
    </div>
  );
};

export default Contact;